import type {
  AssistantRequestBody,
  AssistantResponse,
  CreateDocumentRequest,
  CreateDocumentResponse,
  NevisignDocumentExport,
  NevisignDocumentsResponse,
  OfficeAssistantRequest,
  OfficeAssistantResponse,
  ReaderVersionManifest,
} from "../types.js";

export type NevisignAuthSnapshot = {
  token?: string | null;
  credentials?: RequestCredentials;
};

type AuthProvider = () =>
  | NevisignAuthSnapshot
  | Promise<NevisignAuthSnapshot>;

let authProvider: AuthProvider = () => ({});

/** Wire Bearer token + cookie credentials from the host app (platform login). */
export function setNevisignAuthProvider(fn: AuthProvider): void {
  authProvider = fn;
}

/** Ensure platform reader calls target `/api/v1/*`, not legacy `/api/*`. */
export function normalizeNevisignApiBase(apiBase: string): string {
  const trimmed = apiBase.replace(/\/$/, "").trim();
  if (!trimmed) return "/api/v1";
  if (/\/api\/v1$/i.test(trimmed)) return trimmed;

  // Local API process root — prefer Vite-relative proxy path (avoids CORS on :5174).
  if (/^https?:\/\/(127\.0\.0\.1|localhost):4001(\/api)?$/i.test(trimmed)) {
    return "/api/v1";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return `${trimmed}/api/v1`;
  }

  if (trimmed === "/api") return "/api/v1";
  if (trimmed.startsWith("/")) return `${trimmed}/api/v1`;

  return trimmed;
}

export function joinNevisignUrl(apiBase: string, path: string): string {
  const base = normalizeNevisignApiBase(apiBase);
  const raw = path.startsWith("/") ? path : `/${path}`;
  if (!base) return raw;
  if (/\/api\/v1$/i.test(base)) {
    const stripped = raw.replace(/^\/api(?=\/)/, "");
    return `${base}${stripped}`;
  }
  if (raw.startsWith("/api/")) return `${base}${raw}`;
  return `${base}/api${raw}`;
}

export async function nevisignFetch(
  apiBase: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const auth = await Promise.resolve(authProvider());
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (auth.token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }
  return fetch(joinNevisignUrl(apiBase, path), {
    ...init,
    headers,
    credentials: init.credentials ?? auth.credentials ?? "include",
  });
}

export function getMockDocuments(): NevisignDocumentsResponse {
  return { documents: [], total: 0 };
}

export type FetchDocumentsResult = {
  data: NevisignDocumentsResponse;
  source: "api" | "empty" | "error";
  error?: string;
};

function emptyDocs(): NevisignDocumentsResponse {
  return { documents: [], total: 0 };
}

/**
 * Fetch document list from platform GET /api/v1/documents.
 * Empty list on error — never invents sample contracts.
 */
export async function fetchNevisignDocuments(
  apiBase: string,
  opts?: { signal?: AbortSignal; search?: string; pageSize?: number },
): Promise<FetchDocumentsResult> {
  if (!apiBase.trim()) {
    return { data: emptyDocs(), source: "empty" };
  }

  try {
    const qs = new URLSearchParams();
    const search = opts?.search?.trim();
    if (search) qs.set("search", search);
    qs.set("pageSize", String(opts?.pageSize ?? 100));
    const path = `/api/documents?${qs.toString()}`;
    const res = await nevisignFetch(apiBase, path, {
      method: "GET",
      signal: opts?.signal,
      cache: "no-store",
    });
    if (!res.ok) {
      const authError =
        res.status === 401
          ? "نشست منقضی شده است. دوباره وارد شوید."
          : res.status === 403
            ? "دسترسی به اسناد این سازمان ندارید."
            : null;
      const routeError =
        res.status === 404
          ? "آدرس API نادرست است. در تنظیمات مقدار /api/v1 را بررسی کنید."
          : null;
      return {
        data: emptyDocs(),
        source: "error",
        error: authError ?? routeError ?? `HTTP ${res.status}`,
      };
    }
    const json = (await res.json()) as NevisignDocumentsResponse;
    if (!Array.isArray(json.documents)) {
      return { data: emptyDocs(), source: "error", error: "پاسخ نامعتبر" };
    }
    return {
      data: json,
      source: json.documents.length ? "api" : "empty",
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "خطای شبکه";
    return { data: emptyDocs(), source: "error", error: message };
  }
}

export type SendAssistantResult = AssistantResponse & {
  source: "api";
};

function normalizeApiBase(apiBase: string): string {
  return apiBase.replace(/\/$/, "").trim();
}

function persianNetworkError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const lower = raw.toLowerCase();
  if (
    lower.includes("failed to fetch") ||
    lower.includes("network") ||
    lower.includes("load failed") ||
    lower.includes("networkerror")
  ) {
    return `ارتباط با سرور نویساین برقرار نشد. جزئیات: ${raw}`;
  }
  return `خطا در ارتباط با دستیار: ${raw}`;
}

/** POST /api/v1/assistant — platform global assistant adapter. */
export async function sendAssistantMessage(
  apiBase: string,
  body: AssistantRequestBody & { documentTitle?: string },
): Promise<SendAssistantResult> {
  const base = normalizeApiBase(apiBase);
  if (!base) {
    throw new Error("آدرس پایه API تنظیم نشده است.");
  }

  let res: Response;
  try {
    res = await nevisignFetch(base, "/api/assistant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: body.message,
        path: body.path ?? "/reader",
        focused_domain_id: body.focused_domain_id ?? null,
        recent: body.recent,
      } satisfies AssistantRequestBody),
    });
  } catch (err) {
    throw new Error(persianNetworkError(err));
  }

  let json: AssistantResponse & { error?: string } = {
    reply: "",
    actions: [],
    changes: [],
  };
  try {
    json = (await res.json()) as AssistantResponse & { error?: string };
  } catch {
    /* non-JSON body */
  }

  if (!res.ok) {
    const detail =
      (typeof json.reply === "string" && json.reply.trim()) ||
      (typeof json.error === "string" && json.error.trim()) ||
      `سرور پاسخ HTTP ${res.status} داد.`;
    throw new Error(detail);
  }

  return {
    reply: json.reply || "پاسخی دریافت نشد.",
    actions: json.actions ?? [],
    changes: json.changes ?? [],
    focus: json.focus,
    source: "api",
  };
}

export type SendOfficeAssistantResult = OfficeAssistantResponse & {
  source: "api";
};

/** POST /api/v1/office/assistant — document-aware editor assistant. */
export async function sendOfficeAssistantMessage(
  apiBase: string,
  body: OfficeAssistantRequest,
): Promise<SendOfficeAssistantResult> {
  const base = normalizeApiBase(apiBase);
  if (!base) {
    throw new Error("آدرس پایه API تنظیم نشده است.");
  }

  const payload: OfficeAssistantRequest = {
    message: body.message,
    document_text: body.document_text,
    selection: body.selection ?? null,
    context: body.context,
    outline: body.outline,
    paragraph: body.paragraph,
    surrounding: body.surrounding,
    document_id: body.document_id,
    mode: body.mode ?? "auto",
    recent: body.recent,
    user_key: body.user_key,
    section_index: body.section_index,
    focus_section_ids: body.focus_section_ids,
    document_fingerprint: body.document_fingerprint,
  };

  let res: Response;
  try {
    res = await nevisignFetch(base, "/api/office/assistant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw new Error(persianNetworkError(err));
  }

  let json: OfficeAssistantResponse = { reply: "", edits: [] };
  try {
    json = (await res.json()) as OfficeAssistantResponse;
  } catch {
    /* non-JSON */
  }

  if (!res.ok) {
    const detail =
      (typeof json.reply === "string" && json.reply.trim()) ||
      (typeof json.error === "string" && json.error.trim()) ||
      `سرور پاسخ HTTP ${res.status} داد.`;
    throw new Error(detail);
  }

  return {
    reply: json.reply || "پاسخی دریافت نشد.",
    edits: Array.isArray(json.edits) ? json.edits : [],
    assistant: json.assistant,
    usage: json.usage,
    source: "api",
  };
}

/** GET /api/v1/office/sync/export/:id — fetch document body for viewing. */
export async function fetchNevisignDocumentExport(
  apiBase: string,
  documentId: string,
  opts?: { signal?: AbortSignal },
): Promise<NevisignDocumentExport> {
  const base = normalizeApiBase(apiBase);
  if (!base) {
    throw new Error("آدرس پایه API تنظیم نشده است.");
  }
  const id = documentId.trim();
  if (!id) {
    throw new Error("شناسه سند نامعتبر است.");
  }

  let res: Response;
  try {
    res = await nevisignFetch(
      base,
      `/api/office/sync/export/${encodeURIComponent(id)}`,
      {
        method: "GET",
        signal: opts?.signal,
        cache: "no-store",
      },
    );
  } catch (err) {
    throw new Error(persianNetworkError(err));
  }

  let json: NevisignDocumentExport = {
    document_id: id,
    body_text: "",
  };
  try {
    json = (await res.json()) as NevisignDocumentExport;
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(
      json.error?.trim() || `دریافت سند ناموفق بود (HTTP ${res.status}).`,
    );
  }

  const body = (json.body_cm || json.body_text || "").trim();
  if (!body) {
    throw new Error("متن سند از سرور خالی برگشت.");
  }

  return {
    ...json,
    document_id: json.document_id || id,
    body_text: json.body_text || body,
    body_cm: json.body_cm || body,
  };
}

/** POST /api/v1/documents — create / import document into Nevisign. */
export async function createNevisignDocument(
  apiBase: string,
  body: CreateDocumentRequest,
): Promise<CreateDocumentResponse> {
  const base = normalizeApiBase(apiBase);
  if (!base) {
    throw new Error("آدرس پایه API تنظیم نشده است.");
  }

  let res: Response;
  try {
    res = await nevisignFetch(base, "/api/documents", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mode: body.mode ?? "import",
        source: body.mode === "ai" ? undefined : "reader",
        skip_ai: body.mode !== "ai",
        text: body.text,
        title: body.title,
        attachments: body.attachments,
        allocate_number: body.allocate_number ?? false,
        user_key: body.user_key ?? "reader-app",
      }),
    });
  } catch (err) {
    throw new Error(persianNetworkError(err));
  }

  let json: CreateDocumentResponse = { id: "" };
  try {
    json = (await res.json()) as CreateDocumentResponse;
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(
      json.error?.trim() || `ذخیره در نویساین ناموفق بود (HTTP ${res.status}).`,
    );
  }

  if (!json.id) {
    throw new Error("سرور شناسهٔ سند برنگرداند.");
  }

  return json;
}

/** Fetch OTA version manifest (or platform GET /api/v1/reader/version). */
export async function fetchReaderVersionManifest(
  url: string,
  opts?: { signal?: AbortSignal },
): Promise<ReaderVersionManifest> {
  const res = url.startsWith("/")
    ? await nevisignFetch("", url, {
        method: "GET",
        signal: opts?.signal,
        cache: "no-store",
      })
    : await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: opts?.signal,
        cache: "no-store",
        credentials: "include",
      });
  if (!res.ok) {
    throw new Error(`بررسی به‌روزرسانی ناموفق (HTTP ${res.status})`);
  }
  const json = (await res.json()) as ReaderVersionManifest;
  if (typeof json.versionCode !== "number" || typeof json.versionName !== "string") {
    throw new Error("فایل version.json نامعتبر است.");
  }
  return {
    ...json,
    apkUrl: typeof json.apkUrl === "string" ? json.apkUrl : "",
  };
}
